import { Dispatch } from 'react';
import { CountryCode, CurrencyCode } from '@dayetopia/library';
import { MainContextActionTypes, CURRENCY_EUR } from '@constants';
import { ProductConfiguration, PromotionObject, ShippingObject } from '@contracts';
import packageJson from '../../package.json';
import getOmsUrl from './getOmsUrl';
import { setOnLocalStorage } from './localStorage';

const ecomDataTimestampPrefix = 'ecomDataTimestamp-';
const ecomDataTimestampKey = `${ecomDataTimestampPrefix}${packageJson.version}`;

/**
 * Removes legacy ecomDataTimestamp entries from localStorage
 */
function cleanLegacyTimestamps() {
  const { length } = localStorage;
  const keysToRemove = [];
  for (let i = 0; i < length; i += 1) {
    const key = localStorage.key(i);
    if (key?.startsWith(ecomDataTimestampPrefix) && key !== ecomDataTimestampKey) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach((key) => localStorage.removeItem(key));
}

export function getEcommerceData(countryCode: string, dispatch: Dispatch<MainContextActionTypes>) {
  const oms = getOmsUrl();
  const url = `${oms}/ecommerce/data`;
  const options = {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json; charset=utf-8'
    }
  };
  fetch(url, options)
    .then((res) => res.json())
    .then((json) => {
      const { shippingFees, promotions, configurations } = json;
      const countryShippingFees =
        shippingFees?.find((item: { countryCode: string }) => item.countryCode === countryCode) ??
        shippingFees.find((item: { countryCode: string }) => item.countryCode === CountryCode.GB);
      if (!promotions || !configurations || !countryShippingFees) return;
      updateEcomData(promotions, configurations, dispatch, countryShippingFees, countryCode as CountryCode);
    })
    .catch((err) => {
      throw err;
    });
}

function updateEcomData(
  promotions: PromotionObject[],
  configurations: ProductConfiguration[],
  dispatch: Dispatch<MainContextActionTypes>,
  countryShippingFees?: ShippingObject,
  countryCode?: CountryCode
) {
  setOnLocalStorage('promotions', promotions, true);
  setOnLocalStorage('configurations', configurations, true);

  if (countryShippingFees) {
    // Bulgaria uses EUR directly (not BGN) - override API response
    const isBulgaria = countryCode === CountryCode.BG;
    const currency =
      isBulgaria || countryShippingFees.currencyCode === CurrencyCode.BGN
        ? CURRENCY_EUR
        : {
            letterCode: countryShippingFees.currencyCode,
            symbol: countryShippingFees.currencySymbol
          };
    dispatch({ type: 'setCurrency', data: currency });
    dispatch({ type: 'setShippingFees', data: countryShippingFees.shippingFees });
    dispatch({ type: 'setFreeShippingRequirement', data: countryShippingFees.freeShippingRequirement ?? 50 });

    const date = new Date();
    setOnLocalStorage(ecomDataTimestampKey, date.getTime().toString());
    cleanLegacyTimestamps();
  }
}
